import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from 'lucide-react';
import { Sale } from '../../api/SalesAPI';
import { getSaleDiscountTooltipLines, hasAnyDiscount } from '../../utils/saleLineItems';
import Tooltip from '../Tooltip/Tooltip';

type Props = {
  sale: Pick<Sale, 'saleOffer' | 'primaryLineOffer' | 'additionalItems' | 'currency' | 'dressName'>;
  className?: string;
};

function SaleDiscountIndicator({ sale, className = '' }: Props) {
  const { t } = useTranslation();

  const tooltipLines = useMemo(() => getSaleDiscountTooltipLines(sale), [sale]);

  if (!hasAnyDiscount(sale)) {
    return null;
  }

  const saleLevelLabel = t('sales.list.discount_tooltip_sale') || 'Sale discount';
  const ariaLabel = t('sales.list.discount_applied') || 'Discount applied';

  const tooltipContent = (
    <div className="space-y-1">
      {tooltipLines.map((line, index) => (
        <div key={`${line.label ?? 'sale'}-${index}`}>
          {line.saleLevel ? `${saleLevelLabel}: ` : line.label ? `${line.label}: ` : null}
          {line.summary}
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip content={tooltipContent} position="bottom" contentClassName="max-w-xs">
      <span
        className={`inline-flex cursor-default ${className}`.trim()}
        aria-label={ariaLabel}
        role="img"
      >
        <Tag className="h-3.5 w-3.5 text-blue-600" aria-hidden />
      </span>
    </Tooltip>
  );
}

export default SaleDiscountIndicator;
